import React, { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link2, RefreshCw } from 'lucide-react';
import { toaster } from './toast';
import { extractEngineSyncErrorMessage, useSyncServiceToEngine } from '../../hooks/useServices';
import type { EngineSyncState } from '../../utils/engineMapping';

/**
 * SAM-198 — "Connect to engine" for a service the booking engine does not know yet (no engine
 * service id / resource mapping, see utils/engineMapping). Runs the same POST
 * /bookings/services/{id}/engine-sync as the editor's "Sync now" (useSyncServiceToEngine, which
 * polls until the sync settles and toasts success / failure / timeout itself). Disabled while it
 * runs and whenever the caller says so (e.g. the editor is saving or has unsaved edits: the sync
 * reads the SAVED service). New Tailwind component (SAM-175 freeze).
 */
export const EngineConnectButton: React.FC<{
  serviceId: string;
  disabled?: boolean;
  /** Shown as the tooltip while disabled. */
  disabledReason?: string;
  className?: string;
}> = ({ serviceId, disabled, disabledReason, className = '' }) => {
  const queryClient = useQueryClient();
  const sync = useSyncServiceToEngine();
  const cancel = sync.cancel;
  useEffect(() => () => cancel(), [cancel]);

  const busy = sync.isPending;
  const onClick = (e: React.MouseEvent) => {
    // Lives inside clickable list rows: connecting must not also open the service.
    e.stopPropagation();
    if (busy || disabled) return;
    sync.mutate(serviceId, {
      onSuccess: (outcome) => {
        queryClient.invalidateQueries({ queryKey: ['service-detail', serviceId] });
        // 'neutral' (no reliable finished-signal) is silent in the hook; say something here.
        if (outcome.kind === 'neutral') {
          toaster.info('Connecting to the booking engine. It can take a minute to show as synced.', { autoHideDuration: 4000 });
        }
      },
      onError: (error) => {
        toaster.negative(extractEngineSyncErrorMessage(error), { autoHideDuration: 4000 });
      },
    });
  };

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={(e) => e.stopPropagation()}
      disabled={busy || disabled}
      title={disabled && disabledReason ? disabledReason : 'Create this service in the booking engine now'}
      aria-busy={busy || undefined}
      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-[8px] border border-solid border-[var(--tenant-primary,#005488)] bg-white text-[12px] font-semibold text-[var(--tenant-primary,#005488)] whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-default ${className}`}
    >
      {busy ? <RefreshCw size={13} className="animate-spin" /> : <Link2 size={13} />}
      {busy ? 'Connecting…' : 'Connect to engine'}
    </button>
  );
};

const PILL: Record<EngineSyncState, { label: string; cls: string }> = {
  synced: { label: 'Synced', cls: 'bg-[var(--ok-bg)] text-[var(--ok-fg)]' },
  unsynced: { label: 'Not connected', cls: 'bg-[var(--warn-bg-2,#fff5e0)] text-[var(--warn-fg)]' },
  unknown: { label: 'Checking…', cls: 'bg-[var(--line-subtle)] text-[var(--ink-500)]' },
};

/** Engine status pill, plus the connect button when the service is not synced. */
export const EngineSyncBadge: React.FC<{
  state: EngineSyncState;
  serviceId: string;
  disabled?: boolean;
  disabledReason?: string;
  /** SAM-1312 (U19): false for who may not edit services: the pill stays, the connect button does not. Default true. */
  canConnect?: boolean;
}> = ({ state, serviceId, disabled, disabledReason, canConnect = true }) => (
  <span className="inline-flex flex-wrap items-center gap-2">
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-semibold whitespace-nowrap ${PILL[state].cls}`}>
      {PILL[state].label}
    </span>
    {state === 'unsynced' && canConnect && <EngineConnectButton serviceId={serviceId} disabled={disabled} disabledReason={disabledReason} />}
  </span>
);
